import { InteractionState } from '@cadstrom/renderer';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import type { DrawingProperties, DrawingPropertyValue, ElementSelectionArgs } from '@cadstrom/renderer';
import type { CoreModel } from '@cadstrom/sigma-engine-shared';
import type { CadRendererSyncScope } from '../sync/broadcast-manager.js';

export type InteractionManagerContextValue = {
	interactionManager: InteractionState;
	selection: ElementSelectionArgs['allSelectionArgs'];
	highlight: ElementSelectionArgs['allSelectionArgs'];
	setHighlight: (highlight: ElementSelectionArgs['allSelectionArgs'] | null) => void;
	setSelection: (selection: ElementSelectionArgs['allSelectionArgs'] | null) => void;
	syncScope?: CadRendererSyncScope;
};
export const InteractionManagerContext = createContext<InteractionManagerContextValue | null>(null);

/**
 * Reads the current renderer interaction manager context.
 */
export const useInteractionManagerContext = () => {
	const value = use(InteractionManagerContext);
	invariant(value !== null, 'InteractionManagerContext not provided');
	return value;
};

export type UseSetUpInteractionManagerContextOptions = {
	syncScope?: CadRendererSyncScope;
};

/**
 * Creates the renderer interaction manager and keeps React selection/highlight state in sync with it.
 * Returns null when no model is provided (e.g. while it is still loading), so the
 * result can be passed directly to the context provider without changing the tree shape.
 */
export function useSetUpInteractionManagerContext(
	projectModel: CoreModel,
	options?: UseSetUpInteractionManagerContextOptions,
): InteractionManagerContextValue;
export function useSetUpInteractionManagerContext(
	projectModel: CoreModel | undefined,
	options?: UseSetUpInteractionManagerContextOptions,
): InteractionManagerContextValue | null;
export function useSetUpInteractionManagerContext(
	projectModel: CoreModel | undefined,
	options: UseSetUpInteractionManagerContextOptions = {},
): InteractionManagerContextValue | null {
	const manager: InteractionState | null = useMemo(
		() => (projectModel ? new InteractionState(projectModel.layerSets) : null),
		[projectModel],
	);
	const [stateValue, setStateValue] = useState<Pick<InteractionManagerContextValue, 'selection' | 'highlight'>>({
		selection: new Map(),
		highlight: new Map(),
	});

	// Helper function to compare two selection/highlight states
	const isSameState = useCallback(
		(
			current: ElementSelectionArgs['allSelectionArgs'] | undefined,
			newState: ElementSelectionArgs['allSelectionArgs'] | null,
		): boolean => {
			if (!current && !newState) {
				return true;
			}
			if (!current || !newState) {
				return false;
			}

			if (current.size !== newState.size) {
				return false;
			}

			for (const [property, values] of current.entries()) {
				const newValues = newState.get(property);
				if (!newValues || newValues.size !== values.size) {
					return false;
				}

				for (const value of values) {
					if (!newValues.has(value)) {
						return false;
					}
				}
			}
			return true;
		},
		[],
	);

	// Helper function to update state (highlight or selection)
	const updateState = useCallback(
		(
			newState: ElementSelectionArgs['allSelectionArgs'] | null,
			getCurrentState: () => ElementSelectionArgs['allSelectionArgs'] | undefined,
			clearState: () => void,
			setElementsByProperty: (property: DrawingProperties, value: DrawingPropertyValue, add: boolean) => void,
		) => {
			const currentState = getCurrentState();

			// Only update if the state is actually different
			if (!isSameState(currentState, newState)) {
				if (newState === null) {
					clearState();
				} else {
					// Set the state directly by updating the internal state
					// We need to clear first, then set each property/value combination
					clearState();
					for (const [property, values] of newState.entries()) {
						for (const value of values) {
							setElementsByProperty(property, value, true);
						}
					}
				}
			}
		},
		[isSameState],
	);

	const setHighlight = useCallback(
		(highlight: ElementSelectionArgs['allSelectionArgs'] | null) => {
			if (!manager) {
				return;
			}
			updateState(
				highlight,
				() => manager.getCurrentHighlight(),
				() => manager.clearHighlight(),
				(property, value, add) => manager.highlightElementsByProperty(property, value, add),
			);
		},
		[updateState, manager],
	);

	const setSelection = useCallback(
		(selection: ElementSelectionArgs['allSelectionArgs'] | null) => {
			if (!manager) {
				return;
			}
			updateState(
				selection,
				() => manager.getCurrentSelection(),
				() => manager.clearSelection(),
				(property, value, add) => manager.selectElementsByProperty(property, value, add),
			);
		},
		[updateState, manager],
	);

	useEffect(() => {
		if (!manager) {
			return;
		}
		const activeManager = manager;

		function updateSelection() {
			const selection = activeManager.getCurrentSelection();
			setStateValue((currentValue) => ({
				...currentValue,
				selection: selection ?? new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
			}));
		}

		activeManager.on('clearSelection', updateSelection);
		activeManager.on('selectElementsByProperty', updateSelection);

		function updateHighlight() {
			const highlight = activeManager.getCurrentHighlight();
			setStateValue((currentValue) => ({
				...currentValue,
				highlight: highlight ?? new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
			}));
		}

		activeManager.on('clearHighlight', updateHighlight);
		activeManager.on('highlightElementsByProperty', updateHighlight);

		setStateValue({
			highlight: activeManager.getCurrentHighlight() ?? new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
			selection: activeManager.getCurrentSelection() ?? new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
		});

		return () => {
			activeManager.removeListener('clearSelection', updateSelection);
			activeManager.removeListener('selectElementsByProperty', updateSelection);
			activeManager.removeListener('clearHighlight', updateHighlight);
			activeManager.removeListener('highlightElementsByProperty', updateHighlight);
		};
	}, [manager]);

	if (!manager) {
		return null;
	}

	return {
		...stateValue,
		setHighlight,
		setSelection,
		interactionManager: manager,
		syncScope: options.syncScope,
	};
}

/**
 * Derives whether any property/value pair in a filter is selected or highlighted in the renderer.
 */
export const useSelectionStateContextWithFilter = (filter: [DrawingProperties, string][]) => {
	const selectionState = useInteractionManagerContext();

	const selected = useMemo(
		() => filter.some(([prop, value]) => selectionState.selection.get(prop)?.has(value)),
		[filter, selectionState.selection],
	);
	const highlighted = useMemo(
		() => filter.some(([prop, value]) => selectionState.highlight.get(prop)?.has(value)),
		[filter, selectionState.highlight],
	);

	return { selected, highlighted };
};
